import SelectComponent, { SelectItem } from '@components/inputs/select/Select.component';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import { auditDetailsQuery } from '@store/audits/audit-details/audits-details.query';
import { AuditOffer as AuditOfferModel } from '@store/audits/audit-offer/audit-offer.model';
import { auditsOfferService } from '@store/audits/audit-offer/audit-offer.services';
import { auditsofferQuery } from '@store/audits/audit-offer/audits-offer.query';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import { RepositionDTO } from '@store/audits/audits-information/audits-information.model';
import { AuditOperationStepTypeEnum } from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationQuery } from '@store/audits/audits-operation/audits-operation.query';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { auditsService } from '@store/audits/audits.service';
import { sortByPosition } from '@utils/sortData';
import { useResponsive } from '@utils/useResponsive';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { finalize, switchMap } from 'rxjs';
import OfferDimensionContent from '../../components/OfferDimensionContent';
import { useDragnDrop } from './utils/useDragnDrop';


interface AuditOfferProps {
  isTemplate?: boolean;
}
const AuditOffer = (props: AuditOfferProps) => {
  const { t } = useTranslation();
  const { isTemplate } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [CSVLoading, setCSVLoading] = useState(false);
  const [isDiseabled, setIsDiseabled] = useState<boolean>(true);
  const [dragnDropItems, setDragnDropItems] = useState<AuditOfferModel[]>([]);
  const [isExpanded, setIsExpended] = useState<number[]>([]);
  const { auditId } = useParams();
  const { onDragEnd, getListStyle } = useDragnDrop();
  const [version, setVersion] = useState<number>();
  const { isTablet } = useResponsive();


  const [{ auditDetails }] = useObservable(auditDetailsQuery.auditDetails$);
  const [{ auditsInfoOP }] = useObservable(auditsOperationQuery.auditsOP$);
  const [{ auditsOffer }] = useObservable(auditsofferQuery.auditsofferList$);

  const getOpName = (OperationId: string | number) =>
    auditsInfoOP.filter(op => op.id === OperationId)
      ?.flatMap(op => (op.container?.name ?? op?.containerCustom) + ' - ' + (op.waste?.name ?? op?.wasteCustom))[0];

  const handleVersionChange: SelectItem[] =
    _.range(1, auditDetails.currentVersion + 1).map(it => (
      { label: t('audits.offerDimension.version', { value: it }), value: it }
    ));

  const toggleExpended = (offerId: number) => {
    if (isExpanded.includes(offerId)) {
      setIsExpended(isExpanded.filter(id => id !== offerId));
    } else
      setIsExpended([...isExpanded, offerId]);
  };

  useEffect(() => {
    emitOnce(() => {
      auditsOperationService.setStep(AuditOperationStepTypeEnum.PRECO);
    });
    if (auditDetails.id) {
      setLoading(true);
      auditsOfferService.getAuditOffer(auditDetails.id, version ?? auditDetails.currentVersion).pipe(
        switchMap(() => auditsOperationService.getAuditsInformationOP(auditDetails.id)),
        finalize(() => setLoading(false))
      ).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }

    return () => {
      auditsOfferService.resetStore();
    };
  }, [auditDetails, version, enqueueSnackbar]);

  useEffect(() => {
    if (auditDetails.id) {
      setVersion(auditDetails.currentVersion);
    }
  }, [auditDetails]);

  useEffect(() => {
    if (auditsOffer) {
      setDragnDropItems(auditsOffer.sort(sortByPosition).filter(item =>
        (!item.auditOffer.length && version !== auditDetails.currentVersion) ? false : item
      ));
    }
  }, [auditDetails.currentVersion, auditsOffer, version]);

  const handleSavePosition = () => {
    if (!isDiseabled && auditId) {
      const positionData: RepositionDTO = {
        data: dragnDropItems.map((item, idx) => ({ ressourceId: item.auditOperationId, position: idx, isOperation: true })),
      };
      auditsInformationService.updatePosition(positionData, auditId).subscribe();
    }
    setIsDiseabled(!isDiseabled);
  };

  const saveVersion = () => {
    if (auditId) {
      auditsService.saveVersionAudit(auditId).pipe(finalize(() => setLoading(false))).subscribe({
        next: () => {
          enqueueSnackbar(t('audits.success.change-version'), { variant: 'success' });
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });

    }
  };

  const handleExportCSV = () => {
    setCSVLoading(true);
    auditsOfferService.getAuditOfferCSV(auditId!)
      .pipe(finalize(() => setCSVLoading(false)))
      .subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <Stack flex={1} width="100%" py={'20px'} spacing={2} sx={{ backgroundColor: 'white' }} mt='20px' borderRadius='2px' mb='20px'>
      <Stack direction={isTablet ? 'column' : 'row'} spacing={2} alignItems={isTablet ? 'start' : 'center'} justifyContent='space-between' px={'20px'} >
        <Typography fontSize="20px" fontWeight="700" flex={1}>
          {t('audits.global.offer')}
        </Typography>
        {!isTemplate && (
          <Stack direction="row" spacing={2}>
            <SelectComponent
              color="primary"
              size="small"
              items={handleVersionChange}
              handleChange={(version) => setVersion(version)}
              value={version || auditDetails.currentVersion}
            />
            <Button variant="outlined" color="primary" onClick={handleSavePosition}>
              {isDiseabled ? t('audits.buttons.dragnDrop') : t('audits.buttons.save-position')}
            </Button>
            <Button variant="contained" color="primary" onClick={saveVersion}>
              {t('audits.buttons.change-version')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleExportCSV} disabled={CSVLoading}>
              {t('audits.buttons.extract-offer-csv')}
            </Button>
          </Stack>
        )}
      </Stack>
      {
        loading ? <CircularProgress size={30} thickness={5} sx={{
          top: '50%',
          left: '50%',
          position: 'relative',
        }} /> : (
          <DragDropContext onDragEnd={(res) => onDragEnd(res, dragnDropItems, setDragnDropItems)}>
            <Stack
              overflow="auto"
              height={'15rem'}
              py={'15px'}
              px="20px"
              flexGrow={1}
              my="8px"
              spacing={2}
              className="scrollable">
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <Stack
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                    sx={{
                      width: isTablet && !isExpanded.some(opId => dragnDropItems.some(op => op.auditOperationId === opId)) ? 'width' : isTablet ? 'fit-content' : undefined
                    }}
                    spacing={2}
                  >
                    {!dragnDropItems.length && loading ? (
                      <Stack justifyContent='center' alignItems='center' flex={1}>
                        <Typography color='#868F9C' fontSize="15px" fontWeight="100">
                          {t('audits.global.audit-no-data')}
                        </Typography>
                      </Stack>
                    ) : (
                      dragnDropItems.map((auditOfferOp, index) =>
                        <Draggable
                          key={auditOfferOp.auditOperationId.toString()}
                          draggableId={auditOfferOp.auditOperationId.toString()}
                          isDragDisabled={isDiseabled}
                          sx={{ width: '100%' }}
                          index={index}
                        >
                          {(provided) => (
                            <Stack
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                border: !isDiseabled ? '1px dashed #ccc' : 'none',
                              }}
                            >
                              <OfferDimensionContent
                                key={auditOfferOp.auditOperationId}
                                operationId={auditOfferOp.auditOperationId}
                                operationName={getOpName(auditOfferOp.auditOperationId)}
                                offers={auditOfferOp.auditOffer}
                                isTemplate={isTemplate}
                                isDraggable={isDiseabled}
                                isExpanded={isExpanded.includes(auditOfferOp.auditOperationId)}
                                toggleExpended={() => toggleExpended(auditOfferOp.auditOperationId)}
                              />
                            </Stack>
                          )}
                        </Draggable>
                      )
                    )}
                  </Stack>
                )}
              </Droppable>
            </Stack>
          </DragDropContext>
        )
      }
      <Outlet />
    </Stack >
  );
};

export default AuditOffer;
